import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import supabase from '../utils/supabaseClient';
import './ExerciseManagement.css';

const ExerciseManagement = () => {
    const { isDarkTheme } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [exercises, setExercises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [integratedCount, setIntegratedCount] = useState(0);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [languageFilter, setLanguageFilter] = useState('all');
    const [tagFilter, setTagFilter] = useState('');
    const initialView = searchParams.get('view') === 'lessons' ? 'lessons' : 'exercises';
    const [viewMode, setViewMode] = useState(initialView); // 'exercises' or 'lessons'

    // Convenience quick-filters
    const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'pending', 'unfinished'

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const table = viewMode === 'lessons' ? 'lessons' : 'exercises';

            // Total count
            const { count: total } = await supabase
                .from(table)
                .select('*', { count: 'exact', head: true });

            // Integrated count (approved)
            const { count: integrated } = await supabase
                .from(table)
                .select('*', { count: 'exact', head: true })
                .eq('status', 'approved');

            setTotalCount(total || 0);
            setIntegratedCount(integrated || 0);

            // Build query
            let query = supabase
                .from(table)
                .select('*')
                .order('created_at', { ascending: false });

            if (statusFilter !== 'all') {
                query = query.eq('status', statusFilter);
            }

            if (languageFilter !== 'all') {
                query = query.eq('language', languageFilter);
            }

            if (searchQuery.trim()) {
                query = query.ilike('title', `%${searchQuery.trim()}%`);
            }

            if (tagFilter.trim()) {
                query = query.contains('tags', [tagFilter.trim()]);
            }

            // Quick filters
            if (quickFilter === 'pending') {
                query = query.eq('status', 'under_review');
            } else if (quickFilter === 'unfinished') {
                query = query.eq('status', 'draft');
            }

            const { data, error } = await query.limit(50);

            if (error) throw error;
            setExercises(data || []);
        } catch (err) {
            console.error('[ExerciseManagement] Error:', err);
        } finally {
            setLoading(false);
        }
    }, [viewMode, statusFilter, languageFilter, searchQuery, tagFilter, quickFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return '#22c55e';
            case 'under_review': return '#f97316';
            case 'draft': return '#a78bfa';
            default: return '#64748b';
        }
    };

    const formatStatus = (status) => {
        if (!status) return 'Unknown';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    return (
        <div className={`exercise-mgmt ${isDarkTheme ? 'dark' : 'light'}`}>
            {/* Header with counts */}
            <div className="mgmt-header">
                <div className="mgmt-header-left">
                    <h2>{viewMode === 'lessons' ? 'Lessons' : 'Exercises'} Management</h2>
                    <div className="mgmt-counts">
                        <span className="mgmt-count-badge total">Total: {totalCount}</span>
                        <span className="mgmt-count-badge integrated">Integrated: {integratedCount}</span>
                    </div>
                </div>
                <div className="mgmt-header-right">
                    <div className="mgmt-view-toggle">
                        <button
                            className={`mgmt-toggle-btn ${viewMode === 'exercises' ? 'active' : ''}`}
                            onClick={() => setViewMode('exercises')}
                        >
                            Exercises
                        </button>
                        <button
                            className={`mgmt-toggle-btn ${viewMode === 'lessons' ? 'active' : ''}`}
                            onClick={() => setViewMode('lessons')}
                        >
                            Lessons
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick Filter Buttons */}
            <div className="mgmt-quick-filters">
                <button
                    className={`mgmt-quick-btn ${quickFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setQuickFilter('all')}
                >
                    All
                </button>
                <button
                    className={`mgmt-quick-btn ${quickFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => setQuickFilter('pending')}
                >
                    Pending Approvals
                </button>
                <button
                    className={`mgmt-quick-btn ${quickFilter === 'unfinished' ? 'active' : ''}`}
                    onClick={() => setQuickFilter('unfinished')}
                >
                    Unfinished (Drafts)
                </button>
            </div>

            {/* Filters */}
            <div className="mgmt-filters card">
                <div className="mgmt-search">
                    <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="mgmt-filter-row">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All statuses</option>
                        <option value="draft">Draft</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                    </select>
                    <select
                        value={languageFilter}
                        onChange={(e) => setLanguageFilter(e.target.value)}
                    >
                        <option value="all">All languages</option>
                        <option value="en">English</option>
                        <option value="fr">French</option>
                        <option value="it">Italian</option>
                        <option value="ar">Arabic</option>
                    </select>
                    <input
                        type="text"
                        placeholder="Filter by tag..."
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* Exercise List */}
            {loading ? (
                <div className="admin-loading">Loading...</div>
            ) : exercises.length === 0 ? (
                <div className="mgmt-empty card">
                    <p>No {viewMode} found matching your filters.</p>
                    <button
                        className="admin-action-btn primary"
                        onClick={() => navigate(viewMode === 'lessons' ? '/admin/lessons/new' : '/admin/exercises/new')}
                    >
                        + Create {viewMode === 'lessons' ? 'Lesson' : 'Exercise'}
                    </button>
                </div>
            ) : (
                <div className="mgmt-list">
                    {exercises.map((item) => (
                        <div
                            key={item.id}
                            className="mgmt-item card"
                            onClick={() => navigate(viewMode === 'lessons' ? `/admin/lessons/${item.id}/edit` : `/admin/exercises/${item.id}/edit`)}
                        >
                            <div className="mgmt-item-main">
                                <div className="mgmt-item-title">{item.title || 'Untitled'}</div>
                                <div className="mgmt-item-meta">
                                    {item.language && (
                                        <span className="mgmt-meta-tag lang">{item.language.toUpperCase()}</span>
                                    )}
                                    {item.exercise_type && (
                                        <span className="mgmt-meta-tag type">
                                            {item.exercise_type.replace(/_/g, ' ')}
                                        </span>
                                    )}
                                    {Array.isArray(item.tags) && item.tags.slice(0, 3).map((tag, i) => (
                                        <span key={i} className="mgmt-meta-tag">{tag}</span>
                                    ))}
                                    {item.author_name && (
                                        <span className="mgmt-item-author">by {item.author_name}</span>
                                    )}
                                </div>
                            </div>
                            <div className="mgmt-item-right">
                                <span
                                    className="mgmt-status-badge"
                                    style={{
                                        color: getStatusColor(item.status),
                                        borderColor: getStatusColor(item.status),
                                    }}
                                >
                                    {formatStatus(item.status)}
                                </span>
                                <span className="mgmt-item-date">
                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ExerciseManagement;
